/**
 * File upload progress indicator.
 * Shows file info and upload state.
 */

import { File as FileIcon, CheckCircle, XCircle, Loader2 } from 'lucide-react';

export interface FileUploadProgressProps {
  /** File being uploaded */
  file?: File;
  /** Upload status */
  status: 'idle' | 'uploading' | 'success' | 'error';
  /** Progress percentage (0-100) */
  progress?: number;
  /** Error message (when status is 'error') */
  errorMessage?: string;
  /** Custom class name */
  className?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUploadProgress({
  file,
  status,
  progress,
  errorMessage,
  className = '',
}: FileUploadProgressProps) {
  if (status === 'idle' && !file) {
    return null;
  }

  return (
    <div className={`flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg ${className}`}>
      {/* File icon / Status icon */}
      <div className="flex-shrink-0">
        {status === 'uploading' && (
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        )}
        {status === 'success' && (
          <CheckCircle className="h-8 w-8 text-green-500" />
        )}
        {status === 'error' && (
          <XCircle className="h-8 w-8 text-red-500" />
        )}
        {status === 'idle' && file && (
          <FileIcon className="h-8 w-8 text-gray-400" />
        )}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        {file && (
          <>
            <p className="text-sm font-medium text-foreground truncate">
              {file.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatFileSize(file.size)}
            </p>
          </>
        )}

        {/* Progress bar (when uploading) */}
        {status === 'uploading' && typeof progress === 'number' && (
          <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        )}

        {/* Error message */}
        {status === 'error' && errorMessage && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
            {errorMessage}
          </p>
        )}

        {/* Success message */}
        {status === 'success' && (
          <p className="mt-1 text-xs text-green-600 dark:text-green-400">
            Upload complete
          </p>
        )}
      </div>
    </div>
  );
}
